import { Building2, ExternalLink, Server } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useInfra } from '@/features/admin/api'
import type { InfraPlacement } from '@/features/admin/types'
import type { Project } from '../types'

const ENVS = ['production', 'demo', 'staging'] as const

function EnvCounts({ list }: { list: InfraPlacement[] }) {
  const { t } = useTranslation()
  return (
    <span className="flex flex-wrap gap-1.5">
      {ENVS.map((e) => {
        const n = list.filter((p) => p.env === e).length
        return n ? (
          <Badge key={e} tone={e === 'production' ? 'success' : e === 'demo' ? 'info' : 'neutral'}>
            {t(`admin.infra.envs.${e}`)} {n}
          </Badge>
        ) : null
      })}
    </span>
  )
}

export function ProjectServersPanel({ project }: { project: Project }) {
  const { t } = useTranslation()
  const infra = useInfra()
  const active = (infra.data?.placements ?? []).filter((p) => p.projectId === project.id && p.status !== 'decommissioned')
  const servers = (infra.data?.servers ?? []).filter((s) => active.some((p) => p.serverId === s.id))
  const onPrem = active.filter((p) => p.serverId === null)

  return (
    <section className="rounded-xl border bg-card shadow-card">
      <header className="flex flex-wrap items-center gap-2 border-b px-4 py-3">
        <Server className="size-4 text-muted-foreground" aria-hidden />
        <h3 className="flex-1 font-semibold">{t('projects.servers.title')}</h3>
        <Button size="sm" variant="ghost" asChild>
          <Link to="/admin/infra">
            {t('projects.servers.openInfra')}
            <ExternalLink />
          </Link>
        </Button>
      </header>
      <div className="space-y-3 p-4">
        {!infra.data ? (
          <Skeleton className="h-24" />
        ) : !active.length ? (
          <p className="text-sm text-muted-foreground">{t('projects.servers.empty')}</p>
        ) : (
          <>
            <ul className="divide-y rounded-lg border">
              {servers.map((s) => {
                const list = active.filter((p) => p.serverId === s.id)
                return (
                  <li key={s.id} className="flex flex-wrap items-center gap-3 px-3 py-2.5">
                    <Server className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                    <div className="min-w-0 flex-1">
                      <p className="font-medium">{s.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {s.provider} · {s.location}
                      </p>
                    </div>
                    <EnvCounts list={list} />
                    <Button size="icon" variant="outline" className="size-8" asChild>
                      <Link to={`/admin/infra/servers/${s.id}`} aria-label={t('projects.servers.openServer', { name: s.name })}>
                        <ExternalLink />
                      </Link>
                    </Button>
                  </li>
                )
              })}
              {onPrem.map((p) => (
                <li key={p.id} className="flex flex-wrap items-center gap-3 px-3 py-2.5">
                  <Building2 className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium">{p.customerName}</p>
                    <p className="text-xs text-muted-foreground">
                      {t('projects.servers.customerServer')} · {p.hostLabel || p.url}
                    </p>
                  </div>
                  <EnvCounts list={[p]} />
                </li>
              ))}
            </ul>
            <p className="text-xs text-muted-foreground">{t('projects.servers.summary', { servers: servers.length, onPrem: onPrem.length, total: active.length })}</p>
          </>
        )}
      </div>
    </section>
  )
}
